/** @format */

// useMap.ts
import { useToggleZendeskWidget } from '@/@ducks/general/hooks/useToggleZendeskWidget';
import { GTMListingMapMarkerClick } from '@/@ducks/googleTagManager/helpers/google-tag-manager';
import {
  selectorAreas,
  selectorFilters,
  selectorListingQuery,
  selectorLoading,
  selectorPageIndex,
  selectSearchByMap,
} from '@/@ducks/listing/selectors';
import { getBoundsFromCoordinates, getMapCorners } from '@/@ducks/map/utils/getMapCorners';
import { citiesDefaultCoordinates } from '@/components/page/PageListing/citiesDefaultCoordinates';
import { getFilterCount } from '@/components/page/PageListing/components/Filters/filter.utils';
import MapPopup from '@/components/ui/Map/MapPopup';
import { RoomPriceMakersPin, SinglePin } from '@/components/ui/Map/Pin';
import { useWindowWidth } from '@/ducks/general/hooks/useWindowWidth';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { caseUnsensitiveEquals } from '@/utils/helpers/caseUnsensitiveEquals';
import isNil from 'lodash/isNil';
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { MapRef, Marker, ViewState, ViewStateChangeEvent } from 'react-map-gl';
import useSupercluster from 'use-supercluster';
import type { TApiListingMapMarkers, TBuildingRoomsMarker, TRoomMarker } from '../../../api/apiListingMapMarkers';
import { MoveMapEventData, resetMapToDefault } from '../utils/resetMapToDefault';

interface UseMapProps {
  coordinates: { latitude: number; longitude: number };
  markers?: TApiListingMapMarkers;
  showMap: boolean;
  singleMarker?: boolean;
  zoom?: number;
  mapRef: RefObject<MapRef>;
  isMapLoading?: boolean;
  isMapInitiallyLoaded: boolean;
}

export type PopupPosition =
  | 'center'
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom';

export type PopupAnchor = {
  position: PopupPosition;
  verticalOffset?: number;
};

export interface UseMapReturn {
  mapRef: RefObject<MapRef>;
  viewport: ViewState;
  setViewport: React.Dispatch<React.SetStateAction<ViewState>>;
  pins: JSX.Element[];
  getPopupAnchor: (latitude: number, longitude: number) => PopupAnchor | undefined;
  singleMarker?: boolean;
  onViewStateChange?: Function;
  onMoveEnd?: (e: ViewStateChangeEvent) => void;
}

export const useMap = ({
  coordinates,
  markers,
  showMap,
  singleMarker = false,
  zoom = 11,
  mapRef,
  isMapLoading = false,
  isMapInitiallyLoaded,
}: UseMapProps): UseMapReturn => {
  const windowWidth = useWindowWidth();

  const dispatch = useAppDispatch();
  const [viewport, setViewport] = useState<ViewState>({
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    zoom: singleMarker ? 12 : zoom,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });
  const [popupInfo, setPopupInfo] = useState<((TRoomMarker | TBuildingRoomsMarker) & { id: string }) | null>(null);

  const cityName = useAppSelector(selectorListingQuery);
  const isSearchLoading = useAppSelector(selectorLoading);
  const areas = useAppSelector(selectorAreas);
  const searchByMap = useAppSelector(selectSearchByMap);
  const filters = useAppSelector(selectorFilters);
  const pageIndex = useAppSelector(selectorPageIndex);
  const flyToActive = useRef(false);
  useToggleZendeskWidget(!!popupInfo);

  useEffect(() => {
    const MapToggleButton = document.getElementById('MapToggleButton');
    if (!MapToggleButton) return;
    if (popupInfo) {
      MapToggleButton.style.zIndex = '-1';
    } else {
      MapToggleButton.style.zIndex = 'unset';
    }
  }, [popupInfo]);

  useEffect(() => {
    if (showMap && mapRef.current) {
      mapRef.current.resize();
    }
    setViewport(prev => ({
      ...prev,
      width: window.innerWidth,
      height: window.innerHeight,
    }));
  }, [showMap]);

  useEffect(() => {
    if (!areas && !isMapInitiallyLoaded) {
      setViewport({
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        zoom,
        bearing: 0,
        pitch: 0,
        padding: { top: 0, bottom: 0, left: 0, right: 0 },
      });
    }
  }, [areas, isMapInitiallyLoaded]);

  // Auto center map and show markers when areas are reset to default
  useEffect(() => {
    if (!areas && isMapInitiallyLoaded) {
      const eventData: MoveMapEventData = { autoCenterOnMarkersChanged: true };
      resetMapToDefault({ cityName, mapRef, singleMarker, zoom, eventData });
    }
  }, [areas, isMapInitiallyLoaded]);

  useEffect(() => {
    if (!!areas?.length && !!mapRef.current && !searchByMap) {
      const { max_longitude, max_latitude, min_longitude, min_latitude } = areas[0];
      flyToActive.current = true;
      mapRef.current.fitBounds([
        [max_longitude, min_latitude],
        [min_longitude, max_latitude],
      ]);
      mapRef.current.once('moveend', () => {
        flyToActive.current = false; // Reset flyToActive when the flyTo animation ends
      });
      dispatch({ type: 'LISTING_SET_SEARCH_BY_MAP', payload: true });
    }
  }, [areas, searchByMap, mapRef.current]);

  // Auto center map when markers are updated
  useEffect(() => {
    /* We do not auto-zoom if page >= 2 because when a filter is applied,
     * pagination is reset and then auto-zoom is triggered
     *
     * We do not auto-zoom if a filter "areas" is set for two reason:
     * 1. Zoom is now handle manually, so it make sense to disable auto-zoom
     * 2. Markers received by this hook are not filtered by areas, so auto-zoom
     *    will produce wrong result
     */
    if (!markers || markers.length < 1 || !isMapInitiallyLoaded || pageIndex >= 2 || filters.areas) return;

    const eventData: MoveMapEventData = { autoCenterOnMarkersChanged: true };

    // Do not auto center if no filters set
    if (getFilterCount(filters) <= 0) {
      // Instead try to reset to default zoom
      resetMapToDefault({ cityName, mapRef, singleMarker, zoom, eventData });
    }

    const corners = getBoundsFromCoordinates(
      markers?.map(oneMarker => [oneMarker.position.latitude, oneMarker.position.longitude]) ?? [],
    );
    if (!corners) return;

    const bounds: mapboxgl.LngLatBoundsLike = [
      [corners[0][1], corners[0][0]],
      [corners[1][1], corners[1][0]],
    ];

    /* Padding required to not have marker half in the map
     * A max zoom is required, without it, I had a bug with markers disappearing
     */
    mapRef?.current?.fitBounds(bounds, { maxZoom: 14, padding: 50 }, eventData);
  }, [markers, isMapInitiallyLoaded]);

  const getPopupAnchor = (latitude: number, longitude: number): PopupAnchor => {
    const mapBounds = mapRef.current?.getContainer().getBoundingClientRect() as { width: number; height: number };
    const popupWidth: number = 320;
    const popupHeight: number = 440;
    const popupPosition = mapRef.current?.project([longitude, latitude]) as { x: number; y: number };

    const isAbove = popupPosition?.y - popupHeight < 25 && popupPosition?.y >= 0;
    const isBelow = popupPosition?.y + popupHeight > mapBounds?.height - 25;
    const isLeft = popupPosition?.x - popupWidth < 50;
    const isRight = popupPosition?.x + popupWidth > mapBounds?.width - 50;
    const isBrowser = typeof window !== 'undefined';
    const deviceWidth = isBrowser && window.innerWidth;

    let position: PopupPosition = 'center';

    if (Number(deviceWidth) < 481) {
      return { position: 'top' };
    }

    if (isAbove && !isBelow) {
      if (isLeft === isRight) {
        position = 'top';
      } else if (isLeft) {
        position = 'top-left';
      } else if (isRight) {
        position = 'top-right';
      }
    } else if (!isAbove && isBelow) {
      if (isLeft === isRight) {
        position = 'bottom';
      } else if (isLeft) {
        position = 'bottom-left';
      } else if (isRight) {
        position = 'bottom-right';
      }
    } else if (isLeft === isRight) {
      position = 'center';
    } else if (isRight) {
      position = 'right';
    } else if (isLeft) {
      position = 'left';
    }

    let verticalOffset: number | undefined = undefined;

    if (isAbove && isBelow) {
      const aboveOffset = popupPosition?.y - popupHeight;
      const belowOffset = mapBounds?.height - popupPosition?.y - popupHeight;
      verticalOffset = (belowOffset - aboveOffset) / 2;
    }

    return { position, verticalOffset };
  };

  const points = useMemo(
    () =>
      markers?.map(single => {
        const isBuilding = 'rooms' in single;

        if (isBuilding) {
          return {
            type: 'Feature',
            properties: {
              cluster: false,
              category: 'building',
              data: single,

              // Only building
              rooms: single.rooms,
              id: 'building-' + single.rooms.map((m: { code: string; price: number }) => m.code).join('-'),
            },
            geometry: {
              type: 'Point',
              coordinates: [single.position.longitude, single.position.latitude],
            },
          };
        }

        return {
          type: 'Feature',
          properties: {
            cluster: false,
            category: 'single-apartment',
            data: single,

            // Only single apartment
            price: single.price,
            id: 'single-apartment-' + single.code,
          },
          geometry: {
            type: 'Point',
            coordinates: [single.position.longitude, single.position.latitude],
          },
        };
      }),
    [markers],
  );

  /* We need to make sure that the map has loaded correctly for the first time
   * so we can get bounds and get clusters from supercluster
   */
  const getBounds = (): [number, number, number, number] | undefined =>
    mapRef.current?.getMap().getBounds().toArray().flat() as [number, number, number, number] | undefined;
  const [bounds, setBounds] = useState(getBounds());
  useEffect(() => {
    if (!isMapInitiallyLoaded) return;
    setBounds(getBounds());
  }, [mapRef.current, isMapInitiallyLoaded]);

  const { clusters } = useSupercluster({
    points: points || [],
    bounds: bounds,
    zoom: viewport.zoom,
    disableRefresh: isMapLoading,
    options: { radius: 80, minZoom: 5, maxZoom: 15, minPoints: 2, log: false },
  });

  const pins = useMemo(() => {
    if (singleMarker) {
      return [
        <Marker key="marker-single" longitude={coordinates.longitude} latitude={coordinates.latitude} anchor="bottom">
          <SinglePin dataMarker={0} />
        </Marker>,
      ];
    }

    return clusters.map(cluster => {
      const [longitude, latitude] = cluster.geometry.coordinates;
      const { cluster: isCluster, point_count: pointCount, id, price, data } = cluster.properties;

      if (isCluster) {
        const size = pointCount < 10 ? 48 : 64;

        const handleClickCluster = () => {
          if (mapRef.current) {
            flyToActive.current = true;
            mapRef.current?.flyTo({
              center: cluster.geometry.coordinates,
              zoom: viewport.zoom + 1.3,
              speed: 1.5,
              curve: 1,
            });

            mapRef.current?.once('moveend', () => {
              flyToActive.current = false; // Reset flyToActive when the flyTo animation ends
            });
          }
        };

        return (
          <Marker
            style={{ cursor: 'pointer' }}
            key={cluster.id}
            latitude={latitude}
            longitude={longitude}
            onClick={handleClickCluster}
          >
            <div className="cluster-marker" style={{ width: size, height: size }}>
              {pointCount}
            </div>
          </Marker>
        );
      }

      return (
        <Marker
          key={id}
          longitude={longitude}
          latitude={latitude}
          anchor="bottom"
          onClick={e => {
            e.originalEvent.stopPropagation();
            setPopupInfo(null);

            if (popupInfo && caseUnsensitiveEquals(cluster.properties.id, popupInfo.id)) {
              return setPopupInfo(null);
            }

            const isBrowser = typeof window !== 'undefined';
            const deviceWidth = isBrowser && window.innerWidth;

            if (Number(deviceWidth) < 481 && mapRef.current) {
              flyToActive.current = true;
              mapRef.current?.flyTo({
                center: cluster.geometry.coordinates,
                offset: [0, -200],
                zoom: viewport.zoom,
                speed: 1.5,
                curve: 1,
                easing: t => t,
              });

              mapRef.current?.once('moveend', () => {
                flyToActive.current = false; // Reset flyToActive when the flyTo animation ends
              });
            }
            const isBuilding = 'rooms' in data && !isNil(data.rooms);
            const buildingTrackingParams = {
              pinType: isBuilding ? 'pin_building' : 'pin_price',
              roomCode: isBuilding ? data?.rooms[0]?.code : data.code,
              price: isBuilding ? data?.rooms[0]?.price : price,
            } as {
              pinType: 'pin_building' | 'pin_price';
              roomCode: string;
              price: number;
            };

            GTMListingMapMarkerClick({ ...buildingTrackingParams });

            return setPopupInfo({ ...data, id });
          }}
        >
          <RoomPriceMakersPin
            selected={popupInfo && caseUnsensitiveEquals(id, popupInfo.id)}
            data={data}
            dataMarker={id}
          />

          {caseUnsensitiveEquals(id, popupInfo?.id) && (
            <MapPopup
              getPopupAnchor={getPopupAnchor}
              setPopupInfo={setPopupInfo}
              popupInfo={popupInfo as (TRoomMarker | TBuildingRoomsMarker) & { id: string }}
            />
          )}
        </Marker>
      );
    });
  }, [singleMarker, clusters?.length, popupInfo, viewport.zoom]);

  const onMoveEnd = (e: ViewStateChangeEvent & MoveMapEventData) => {
    setBounds(getBounds());

    // Action does not come from user
    if (e.autoCenterOnMarkersChanged) return;

    const eventType = e.type;
    const isNotMoveEndEvent = [undefined, 'resize'].includes(eventType);

    if (isNotMoveEndEvent) return;

    const { viewState } = e;
    setViewport(viewState);
    if (!isSearchLoading && !flyToActive.current && (window.innerWidth > 1159 || showMap)) {
      dispatch({ type: 'LISTING_SET_SEARCH_BY_MAP', payload: true });
      const mapBounds = mapRef.current?.getContainer().getBoundingClientRect() as { width: number; height: number };
      const corners = getMapCorners(viewState, mapBounds?.width, mapBounds?.height);

      const newAreas = [
        {
          min_latitude: corners.se[1],
          max_latitude: corners.nw[1],
          max_longitude: corners.se[0],
          min_longitude: corners.nw[0],
        },
      ];

      dispatch({ type: 'LISTING_IS_MOVING_MAP', payload: true });
      dispatch({ type: 'LISTING_SET_MAP_AREA_FILTER', payload: newAreas });
    }
  };

  return {
    mapRef,
    viewport,
    setViewport,
    pins,
    getPopupAnchor,
    onMoveEnd,
  };
};
